'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/nn-extra'

const DropoutNet = dynamic(() => import('../_components/widgets/NNExtraWidgets').then(m => m.DropoutNet), { ssr: false })

export default function Regularization() {
  return (
    <Lesson>
      <Step>
        <H>Taming big models</H>
        <P>
          Neural networks have so many parameters they can memorise their training set: noise, mistakes and all. You saw
          overfitting in Module 6. <strong>Regularisation</strong> is the toolbox for preventing it without making the model
          smaller.
        </P>
        <UL>
          <li>
            <strong>Weight decay</strong>: penalise big weights, so the model prefers smoother explanations.
          </li>
          <li>
            <strong>Early stopping</strong>: stop training when validation loss stops improving.
          </li>
          <li>
            <strong>Dropout</strong>: randomly switch off neurons while training.
          </li>
          <li>
            <strong>Data augmentation</strong>: make more training data by changing it slightly (shift, rotate, crop).
          </li>
        </UL>
      </Step>

      <Step gate="ridge">
        <H>Weight decay</H>
        <P>
          Add the size of the weights to the loss, and gradient descent has two goals: fit the data, and keep the weights
          small. Wild, wiggly fits need big weights, so they get discouraged.
        </P>
        <MB>{'L_{\\text{total}} = L_{\\text{data}} + \\lambda \\sum_k w_k^2'}</MB>
        <P>
          Here it is on a degree-9 polynomial with only 12 data points, about the worst overfitting setup there is.
        </P>
        <Exercise
          id="ridge"
          title="fitRidge"
          {...EX.ridge}
          hints={[
            <>
              Inside the inner <C>map</C>: add <C>{'(i === j && i > 0 ? lambda : 0)'}</C> to the sum.
            </>,
          ]}
        />
        <Callout kind="plain">
          In the gradient, <M>{'\\lambda w^2'}</M> adds <M>{'2\\lambda w'}</M>, which pulls every weight a little towards 0 on
          every step. That’s why it’s called weight <em>decay</em>. In the course’s <C>Adam</C> it’s one option:{' '}
          <C>{'new Adam(params, { lr, weightDecay: 0.01 })'}</C>.
        </Callout>
      </Step>

      <Step gate="early-stopping">
        <H>Early stopping</H>
        <P>
          Watch the validation loss as you train. It falls, then flattens, then creeps back up as the network starts memorising.
          Keep the weights from the best moment, and stop once things haven’t improved for a while (the <em>patience</em>).
        </P>
        <Exercise
          id="early-stopping" timeoutMs={60000}
          title="bestEpoch and shouldStop"
          {...EX.earlyStopping}
          hints={[
            <>
              <C>{'return vec.argmin(valLosses)'}</C>
            </>,
            <>
              The last epoch is <C>valLosses.length - 1</C>. Stop when it is more than <C>patience</C> epochs after{' '}
              <C>bestEpoch(valLosses)</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-dropout">
        <H>Dropout</H>
        <P>
          A strange but powerful idea: on every training step, switch off a random fraction <M>p</M> of the neurons. The network
          can’t rely on any single neuron, so it learns redundant, robust features, as if training a crowd of smaller networks
          that share weights. At test time every neuron works, and the outputs are scaled so their size matches.
        </P>
        <Figure n="7.8" wide caption="Each training step drops a different random set of hidden neurons. Switch to ‘test time’: everyone works.">
          <DropoutNet />
        </Figure>
        <Quiz
          id="q-dropout"
          question="With dropout p = 0.5, the surviving neurons’ outputs are multiplied by 2 during training. Why?"
          options={[
            { label: 'So the total signal has the same average size during training as at test time, when nobody is dropped', correct: true },
            { label: 'To make training faster', why: 'The scaling doesn’t change the speed. It keeps the next layer’s inputs on the same scale in both modes.' },
            { label: 'To punish the neurons that survived', why: 'It’s a rescaling, not a punishment: half the neurons are missing, so each survivor counts double.' },
          ]}
        />
        <Callout kind="note">
          In code it’s one call: <C>h.dropout(0.2)</C> on a Tensor while training, and no dropout when evaluating. You’ll use
          it in the digits project at the end of this module.
        </Callout>
      </Step>

      <Step gate="q-augment">
        <H>Data augmentation</H>
        <P>
          The best regulariser is more data. When you can’t get more, make more: shift each image a pixel, rotate it slightly,
          change its brightness. The label stays the same, and the model learns that those changes don’t matter.
        </P>
        <Quiz
          id="q-augment"
          question="Which augmentation would be a mistake for handwritten digits?"
          options={[
            { label: 'Flipping images upside down', correct: true },
            { label: 'Shifting by a pixel', why: 'A shifted 3 is still a 3. That’s a safe augmentation.' },
            { label: 'Making strokes slightly thicker', why: 'Pen thickness varies anyway, so it’s safe.' },
          ]}
          explain="An upside-down 6 is a 9! Augmentations must keep the label true. Choosing them is a way of telling the model what you know about the problem."
        />
      </Step>
    </Lesson>
  )
}
